/* eslint-disable react-hooks/exhaustive-deps */
import {formatPhoneNumber, handleError} from "helpers"
import {useModel} from "hooks"
import {intersection, uniqBy} from "lodash"
import {reaction} from "mobx"
import {useEffect, useMemo, useState} from "react"
import {profileService} from "services"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {Auth} from "types/auth"
import UserProfile = Auth.UserProfile
import {Modules, useEdularModulesContext} from "@edular/modules"
import {MAP_ADVISOR_ROLE_DEPARTMENT} from "types/contacts"

export const useSmsSenders = () => {
    const [isLoading, setIsLoading] = useState(false)
    const model = useModel()
    const [userCampusIds, setUserCampusIds] = useState<number[]>([])
    const [, setTick] = useState(0)

    useEffect(() => {
        getCurrentUserCampuses()
    }, [])

    useEffect(() => {
        if (model.user?.id) {
            getCurrentUserCampuses()
        }
    }, [model.user?.id, model.user?.profiles?.length])

    useEffect(() => {
        const dispose = reaction(
            () => ({
                phoneIds: model.smsAllPhoneNumbers.map((x) => x.smsPhoneNumbersId),
                userId: model.user?.id,
                userProfileCount: model.user?.profiles?.length
            }),
            () => setTick((t) => t + 1)
        )
        return dispose
    }, [model])

    const getCurrentUserCampuses = async () => {
        try {
            setIsLoading(true)
            const currentUser = model.user
            const userCampuses = currentUser.profiles?.map(({campuses}) => (campuses || []).map(({id}) => id))
            const userCampusIds = userCampuses.flatMap((id) => id)
            setUserCampusIds(userCampusIds)
        } catch (err) {
            handleError(err)
        } finally {
            setIsLoading(false)
        }
    }

    const defaultSmsSenderId = useMemo(() => {
        const globalPhoneCampuses = model.smsAllPhoneNumbers
            .filter((x) => x.isGlobal === 1)
            .map(({campuses}) => (campuses || []).map(({id}) => id))
            .flatMap((id) => id)
        const sameCampusIds = intersection(userCampusIds, globalPhoneCampuses)
        const selectedCampusId = sameCampusIds?.[0]
        if (selectedCampusId) {
            const selectedPhone = model.smsAllPhoneNumbers.find(
                (x) => x.isGlobal === 1 && (x.campuses || []).filter((y) => y.id === selectedCampusId).length > 0
            )
            return selectedPhone?.smsPhoneNumbersId
        }
        return null
    }, [model.smsAllPhoneNumbers, userCampusIds])

    const senderOptions = useMemo(() => {
        return model.smsAllPhoneNumbers
            .filter((x) => x.isGlobal === 1)
            .map((x) => ({
                id: x.smsPhoneNumbersId,
                name: `${formatPhoneNumber(x.phone)}${
                    x.campuses && x.campuses.length > 0 ? ` - ${x.campuses.map((y) => y.name).join(", ")}` : ""
                }`
            }))
    }, model.smsAllPhoneNumbers)

    const campusList = useMemo(() => {
        return uniqBy(
            model.smsAllPhoneNumbers
                .filter((x) => x.isGlobal === 1 && !!x.campuses)
                .map((x) => x.campuses)
                .flatMap((x) => x),
            "id"
        )
    }, model.smsAllPhoneNumbers)

    return {isLoading, defaultSmsSenderId, senderOptions, campusList}
}

export const useSmsSenderProfiles = () => {
    const model = useModel()
    const [smsSenderProfiles, setSmsSenderProfiles] = useState<UserProfile[]>([])
    useEffect(() => {
        ;(async function loadProfiles() {
            const {data} = await profileService.getAll({
                filter: {
                    type: ["staff"],
                    userId: model.smsAllPhoneNumbers.map(({user}) => user?.id).filter(Boolean)
                },
                range: {
                    limit: UNLIMITED_PAGE_SIZE,
                    offset: 0
                },
                linkedObjects: true
            })
            setSmsSenderProfiles(data)
        })()
    }, [model.smsAllPhoneNumbers])
    return smsSenderProfiles
}

export const useCanSendSms = ({
    userId,
    staffRoleId,
    departmentIds
}: {
    userId?: number
    staffRoleId?: number
    departmentIds?: number[]
}) => {
    const model = useModel()
    const {isModuleEnable} = useEdularModulesContext()
    const smsSenderProfiles = useSmsSenderProfiles()
    return useMemo(() => {
        if (!isModuleEnable(Modules.Sms)) return false
        return !!model.smsAllPhoneNumbers.find(({departments, user}) => {
            if (user && userId && user.id === userId) return true
            if (user && staffRoleId) {
                const senderProfiles = smsSenderProfiles.filter((profile) => profile.userId === user.id)
                if (
                    senderProfiles.find((profile) =>
                        profile.departmentSubunits?.find(
                            (subunit) => MAP_ADVISOR_ROLE_DEPARTMENT[staffRoleId] === subunit.departmentId
                        )
                    )
                )
                    return true
            }
            if (departmentIds && departments?.find((dept) => departmentIds.includes(dept.departmentId))) return true
            return false
        })
    }, [model.smsAllPhoneNumbers, smsSenderProfiles, userId, departmentIds, staffRoleId])
}
